/**
 * dcent-faq-accordion.js
 * Phase 3 Wave 4 dcent-native — semantic <details>/<summary> accordion.
 *
 * Markup:
 *   <div class="dcent-faq" data-single-open="true">   <!-- optional single-open -->
 *     <details class="dcent-faq__item">
 *       <summary>Question</summary>
 *       <div>Answer HTML</div>
 *     </details>
 *     ...
 *   </div>
 *
 * 열고 닫힘은 높이 애니메이션이 붙는다 (2026-08-30 MAGE-554).
 * 종전엔 네이티브 <details> 의 즉시 토글이라, 특히 단일 열림에서 다른 항목을 누르면
 * 열려 있던 답변이 한 프레임 만에 사라져 "내용이 잠깐 보였다 없어지는" 것처럼 읽혔다.
 *
 * 네이티브 시맨틱은 그대로 둔다 — 여는 순간 open 을 먼저 켜고 애니메이션을 얹으며,
 * 닫을 때는 애니메이션이 끝난 뒤에 open 을 끈다. 그래서 접힌 답변은 여전히 접근성 트리와
 * 탭 순서에서 빠진다(이 스니펫이 max-height 클리핑 방식을 버리고 <details> 로 온 이유다).
 */

const PANEL_MS = 220;
const PANEL_EASE = "cubic-bezier(0.4, 0, 0.2, 1)";

/* 답변 패널 = summary 바로 다음 형제. 소비처마다 클래스가 달라(referral 은 __faq-a)
 * 클래스가 아니라 구조로 찾는다. 답변이 비어 아예 없는 항목도 있다(그땐 null). */
function panelOf(item) {
  const summary = item.querySelector("summary");
  const next = summary && summary.nextElementSibling;
  return next || null;
}

function prefersStill() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * 패널을 펼치거나 접는다. opening=false 면 애니메이션이 끝난 뒤 open 을 끈다.
 * Web Animations 를 못 쓰거나 모션을 줄이는 설정이면 즉시 상태만 바꾼다.
 */
function slide(item, opening) {
  const panel = panelOf(item);
  if (!panel || prefersStill() || typeof panel.animate !== "function") {
    if (!opening) item.open = false;
    return;
  }

  const running = item.__dcentFaqAnim;
  if (running) running.cancel();

  const full = panel.scrollHeight;
  const prevOverflow = panel.style.overflow;
  panel.style.overflow = "hidden";

  const frames = [
    { height: "0px", opacity: 0 },
    { height: full + "px", opacity: 1 },
  ];
  const anim = panel.animate(opening ? frames : [frames[1], frames[0]], {
    duration: PANEL_MS,
    easing: PANEL_EASE,
  });
  item.__dcentFaqAnim = anim;

  const restore = () => {
    panel.style.overflow = prevOverflow;
    item.__dcentFaqAnim = null;
  };
  anim.addEventListener("cancel", restore);
  anim.addEventListener("finish", () => {
    restore();
    if (!opening) item.open = false;
  });
}

export function initFaqAccordion(root = document) {
  root.querySelectorAll(".dcent-faq").forEach((container) => {
    const singleOpen = container.dataset.singleOpen === "true";
    const items = Array.from(container.querySelectorAll(".dcent-faq__item"));

    items.forEach((item) => {
      const summary = item.querySelector("summary");
      if (!summary) return;

      /* 클릭을 가로채는 이유: 닫을 때 브라우저가 open 을 즉시 꺼 버리면 애니메이션을 걸 대상이
       * 사라진다. 답변이 없는 항목은 애니메이션할 것도 없으니 기본 동작에 맡긴다.
       * 키보드 Enter·Space 도 summary 에서 click 으로 오므로 같은 경로를 탄다. */
      summary.addEventListener("click", (e) => {
        if (!panelOf(item)) return;
        e.preventDefault();
        if (item.open) {
          slide(item, false);
        } else {
          item.open = true;
          slide(item, true);
        }
      });

      /* 단일 열림 — open 이 켜진 순간 나머지를 접는다. toggle 에 두는 이유는 코드가 open 을
       * 직접 켜는 경로(딥링크·다른 스크립트)도 같은 규칙을 타게 하기 위해서다. */
      if (!singleOpen) return;
      item.addEventListener("toggle", () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item && other.open) slide(other, false);
        });
      });
    });
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  document.addEventListener("DOMContentLoaded", () => initFaqAccordion());
}
